import type { Nutrition } from '../api/types.ts';
import type { Lang, Strings } from '../i18n/index.ts';

/**
 * Пищевая ценность количества еды: ккал и граммы, суммой по строкам или пересчётом со 100 г.
 * Компонент равен null, когда ни одна строка его не дала.
 */
export type NutritionAmount = {
  weightG: number;
  kcal: number | null;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
  /** У какой-то строки нет веса или состава: итог — нижняя граница. */
  isPartial: boolean;
};

export function isNutritionEmpty(nutrition: Nutrition | null | undefined): boolean {
  return !nutrition || (nutrition.energyKcal === null && nutrition.proteinG === null && nutrition.fatG === null && nutrition.carbsG === null);
}

export function hasNutrition(amount: NutritionAmount): boolean {
  return amount.kcal !== null || amount.proteinG !== null || amount.fatG !== null || amount.carbsG !== null;
}

export function isAmountEmpty(amount: NutritionAmount): boolean {
  return amount.weightG === 0 && !hasNutrition(amount);
}

const scale = (value: number | null, k: number) => (value === null ? null : value * k);
const add = (a: number | null, b: number | null) => (a === null ? b : b === null ? a : a + b);

/** Значения на 100 г, пересчитанные на `grams`. */
export function scaledNutrition(nutrition: Nutrition, grams: number): NutritionAmount {
  const k = grams / 100;
  return {
    weightG: grams,
    kcal: scale(nutrition.energyKcal, k),
    proteinG: scale(nutrition.proteinG, k),
    fatG: scale(nutrition.fatG, k),
    carbsG: scale(nutrition.carbsG, k),
    isPartial: false,
  };
}

/** Сумма по строкам: строка — вес нетто × количество с составом на 100 г. */
export function totalNutrition(lines: { weightG: number | null; nutrition: Nutrition | null; qty: number }[]): NutritionAmount {
  const sum: NutritionAmount = { weightG: 0, kcal: null, proteinG: null, fatG: null, carbsG: null, isPartial: false };
  for (const line of lines) {
    if (!line.weightG || line.weightG <= 0) {
      sum.isPartial = true;
      continue;
    }
    const grams = line.weightG * line.qty;
    sum.weightG += grams;
    if (isNutritionEmpty(line.nutrition) || !line.nutrition) {
      sum.isPartial = true;
      continue;
    }
    const part = scaledNutrition(line.nutrition, grams);
    sum.kcal = add(sum.kcal, part.kcal);
    sum.proteinG = add(sum.proteinG, part.proteinG);
    sum.fatG = add(sum.fatG, part.fatG);
    sum.carbsG = add(sum.carbsG, part.carbsG);
  }
  return sum;
}

const formatters = new Map<Lang, Intl.NumberFormat>();

/** Не больше одного знака после запятой, хвостовой ноль отброшен. */
export function formatDecimal(value: number, lang: Lang): string {
  let formatter = formatters.get(lang);
  if (!formatter) {
    formatter = new Intl.NumberFormat(lang === 'sr' ? 'sr-Latn-RS' : 'en-GB', { maximumFractionDigits: 1, useGrouping: false });
    formatters.set(lang, formatter);
  }
  return formatter.format(value);
}

/** До килограмма с одним знаком; от килограмма — в килограммах, чтобы БЖУ большой корзины были не длиннее веса. */
export function formatGrams(value: number, lang: Lang): string {
  return value < 1000 ? `${formatDecimal(Math.round(value * 10) / 10, lang)} g` : `${formatDecimal(Math.round(value / 100) / 10, lang)} kg`;
}

export function formatKcal(value: number, strings: Strings): string {
  return `${Math.round(value)} ${strings.kcalUnit}`;
}

/** Граммы до килограмма, выше — килограммы с одним знаком. */
export function formatWeight(grams: number, lang: Lang): string {
  return grams < 1000 ? `${grams} g` : `${formatDecimal(Math.round(grams / 100) / 10, lang)} kg`;
}

/** Одна приглушённая строка для корзины и заказа: вес, ккал и БЖУ по порядку, только известное. */
export function nutritionSummaryLine(amount: NutritionAmount, lang: Lang, strings: Strings): string | null {
  if (isAmountEmpty(amount)) return null;
  const parts: string[] = [];
  if (amount.weightG > 0) parts.push(formatWeight(amount.weightG, lang));
  if (amount.kcal !== null) parts.push(formatKcal(amount.kcal, strings));
  if (amount.proteinG !== null) parts.push(`${strings.proteinShort} ${formatGrams(amount.proteinG, lang)}`);
  if (amount.fatG !== null) parts.push(`${strings.fatShort} ${formatGrams(amount.fatG, lang)}`);
  if (amount.carbsG !== null) parts.push(`${strings.carbsShort} ${formatGrams(amount.carbsG, lang)}`);
  const line = parts.join(' · ');
  return amount.isPartial ? `≈ ${line}` : line;
}
